import { Component, Inject, OnDestroy } from '@angular/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { catchError, EMPTY, finalize, Observable, of, ReplaySubject, switchMap, takeUntil } from 'rxjs';
import { BalanceService } from 'core/services/balance.service';
import { VerifyStatus } from '../../../../auth/core/types/verify-status.enum';
import { GanasSharingStatusModalComponent } from '../ganas-sharing-status-modal/ganas-sharing-status-modal.component';

import { ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { KorviuLoaderComponent } from '../../../../shared/components/korviu-loader/korviu-loader.component';
import { KorviuButtonComponent } from '../../../../shared/components/korviu-button/korviu-button.component';
import { AuthService } from 'core/services/auth.service';
import { User } from '../../../../shared/types/user.model';
import { KorviuListComponent } from '../../../../shared/components/korviu-list/korviu-list.component';
import { KorviuNotificationService } from 'core/services/korviu-notification.service';
import { NetworkError } from 'shared/models/network-error';

const BALANCE_IN_DEFICIT_ERROR = 'ERR_BALANCE_IN_DEFICIT';

@Component({
  selector: 'app-sharing-confirm',
  standalone: true,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    NzModalModule,
    NzButtonModule,
    KorviuLoaderComponent,
    KorviuButtonComponent
  ],
  templateUrl: './sharing-confirm.component.html',
  styleUrls: ['./sharing-confirm.component.scss']
})
export class SharingConfirmComponent implements OnDestroy {
  public sharingIsLoading: boolean;
  private _onDestroy$: ReplaySubject<void>;

  constructor(
    @Inject(NZ_MODAL_DATA)
    public data: { recipientId: number; amountToSend: number; transactionList?: KorviuListComponent },
    public modalRef: NzModalRef,
    private _modalService: NzModalService,
    private _balanceService: BalanceService,
    private _authService: AuthService,
    private _notificationService: KorviuNotificationService
  ) {
    this.sharingIsLoading = false;
    this._onDestroy$ = new ReplaySubject<void>(1);
  }

  public onAcceptClick(): void {
    this._shareGanas();
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }

  private _shareGanas(): void {
    const userId: number = this.data?.recipientId;
    const amount: number = this.data?.amountToSend;
    this.sharingIsLoading = true;

    this._balanceService
      .shareGanamigos(userId, amount)
      .pipe(
        switchMap(() => this._balanceService.loadTransactionSummary(true).pipe(catchError(() => of(null)))),
        switchMap(() => this._updateUserBalance()),
        switchMap(() => {
          this._openGanasStatusModal(VerifyStatus.SUCCESS);
          return of(null);
        }),
        finalize(() => {
          this.sharingIsLoading = false;
        }),
        catchError((error: NetworkError) => {
          if (error?.errorPayload?.messageCode === BALANCE_IN_DEFICIT_ERROR) {
            this._notificationService.showLocalizedError(BALANCE_IN_DEFICIT_ERROR);
            this.modalRef.close(false);
            return EMPTY;
          }

          this._openGanasStatusModal(VerifyStatus.FAIL);
          return of(null);
        }),
        takeUntil(this._onDestroy$)
      )
      .subscribe(() => {
        this.modalRef.close(true);
      });
  }

  private _updateUserBalance(): Observable<User> {
    if (this.data?.transactionList) {
      this.data?.transactionList.reloadData(true);
    }
    return this._authService.getMe(true).pipe(catchError(() => of(null)));
  }

  private _openGanasStatusModal(status: VerifyStatus): void {
    this.modalRef.close();
    this._modalService.create({
      nzContent: GanasSharingStatusModalComponent,
      nzWrapClassName: 'modal-wrap',
      nzData: { status },
      nzCentered: true,
      nzClosable: false,
      nzWidth: 555
    });
  }
}
